<script setup lang="ts">
import { computed, ref } from 'vue'
import { CommonTitle } from '~/components/common'
import { OrderAddressPicker, OrderList, OrderPaymentMethods } from '~/components/order'
import { ButtonMain } from '@caks/uikit/button'
import { useAccountStore } from '@caks/shared/store/store.account'
import { AuthGroup } from '~/components/auth'
import { orderAsseble, orderEstimate, orderPayment } from '@caks/shared/api/user.route'
import { CartItem, PromoCode } from '@caks/shared/types/types.user'
import { ChipTag } from '@caks/uikit/chip'
import { SkeletonChip } from '@caks/uikit/skeleton'
import { estimateErrors } from '@caks/shared/utils/estimateErrors'
import { isOpenNow } from '@caks/shared/utils/checkIsOpen'

const router = useRouter()
const store = useAccountStore()

const titles = {
  delivery: 'Заказ на доставку',
  pickup: 'Заказ на самовывоз',
}

const isMount = ref(false)

const isUserAuth = () => localStorage.getItem('token')
const isAuthVisible = ref(false)

const orderType = (): 'delivery' | 'pickup' => {
  const type = localStorage.getItem('order') as 'delivery' | 'pickup'

  return type
}

const isPayment = ref(false)
const isDeliveryAmount = ref(0)

const checkout = async () => {
  if (isUserAuth()) {
    isPayment.value = true

    try {
      const assemble = await orderAsseble(store.estimate?.id as number)

      console.log(assemble.status)
      if (assemble.status === 'OK') {
        const checkout = new window.YooMoneyCheckoutWidget({
          confirmation_token: assemble.data.url,

          customization: {
            modal: true,
            colors: { control_primary: '#009e50' },
          },

          error_callback: function (error) {
            console.error(error)
          },
        })

        checkout.on('success', () => {
          setTimeout(() => {
            checkout.destroy()
            router.push('/cart/success?order_id=order_id_here')
          }, 1500)
        })

        checkout.on('modal_close', () => {
          isPayment.value = false
        })

        checkout.on('fail', () => {
          checkout.destroy()
        })

        checkout.render().then((res: any) => {
          console.log(res)
        })
      } else {
        console.log(assemble)

        throw new Error(assemble?.description || assemble?.message || 'Ошибка')
      }
    } catch (error) {
      console.error(error)
    }
  } else {
    isAuthVisible.value = true
  }
}

const totalPrice = computed(() => {
  console.log(store.cart)
  return store.cart.reduce((acc, el) => {
    return (acc += el.cost * el.amount)
  }, 0)
})

const desiredTime = ref('')
const phoneOrder = ref('')
const nameOrder = ref('')
const addressId = ref('')
const productError = ref(false)

const onUpdateAddressInfo = ({
  time,
  address,
  name,
  phone,
}: {
  name: string
  phone: string
  address: string
  time: string
}) => {
  desiredTime.value = time
  addressId.value = address

  nameOrder.value = name
  phoneOrder.value = phone
}

onMounted(() => {
  if (!store.estimate) {
    router.push({ path: '/', query: { state: 'cart' } })
  }

  const savedAddressId = localStorage.getItem('addressId') as string

  if (savedAddressId) {
    addressId.value = savedAddressId
  }

  isMount.value = true

  isDeliveryAmount.value = store.estimate?.delivery_amount || 0
})

const checkIsOpen = () => {
  if (!store.estimate) return
  if (orderType() === 'delivery') {
    return isOpenNow(
      store.estimate.pickup_point.delivery_from,
      store.estimate.pickup_point.delivery_to
    )
  } else if (orderType() === 'pickup') {
    return isOpenNow(store.estimate.pickup_point.open_from, store.estimate.pickup_point.open_to)
  }
}

const isButtonDisabled = computed(() => {
  return (
    !desiredTime.value ||
    String(addressId.value).length === 0 ||
    !nameOrder.value ||
    phoneOrder.value.length !== 11 ||
    productError.value ||
    !checkIsOpen()
  )
})

const buttonText = computed(() => {
  if (checkIsOpen()) {
    return `Оформить заказ на ${totalPrice.value - discountValue.value}₽`
  } else {
    return 'Закрыто'
  }
})

const getDiscount = (promo: PromoCode): number | 'free-delivery' => {
  let result: number | 'free-delivery' = promo.sale_amount

  if (promo.activation_rule === 'free_delivery') {
    result = 'free-delivery'
  }

  return result
}

const getPriceWithDiscount = (price: number, discount: number) => {
  return Math.floor((price * discount) / 100)
}

const discountValue = computed(() => {
  let result = 0

  if (store.promo) {
    const discount = getDiscount(store.promo)

    const isMinAmount = store.promo.min_amount < totalPrice.value
    const isNotFreeDelivery = discount !== 'free-delivery'
    const isCartDiscount = store.promo.activation_rule === 'cart'

    if (isMinAmount && isNotFreeDelivery && isCartDiscount) {
      const isPercents = store.promo.activation_type === 'percents'

      const discountValue = isPercents ? getPriceWithDiscount(totalPrice.value, discount) : discount

      result = discountValue
    }
  }

  return result
})

const errorText = ref('')

const makeEstimate = async () => {
  try {
    store.estimate = null
    store.isEstimateLoading = true
    const estimateInfo = await getEstimateResult()

    console.log('estimateInfo', estimateInfo)
    store.setEstimate(estimateInfo)

    store.isEstimateLoading = false

    errorText.value = ''
  } catch (error) {
    const errorInfo = (error as Error).message

    const { code, data } = JSON.parse(errorInfo)

    if (estimateErrors.hasOwnProperty(code)) {
      errorText.value = estimateErrors[code as keyof typeof estimateErrors] as string
    }

    if (code == 22) {
      data.items.forEach((item: string) => {
        errorText.value += ` (${item})`
      })
    }

    store.isEstimateLoading = false
  }
}

const getPreparedCartToEstimate = (items: CartItem[]) => {
  return items.map((el) => {
    return {
      id: el.item.id,
      count: el.amount,
      excluded_ingredients: el.removedModificators,
      base_modifications: el.baseModificators.map((el) => {
        return { id: el.modification_id, ingredient_id: el.variant_id }
      }),
      extra_modifications: el.extraModificators,
    }
  })
}

const getEstimateResult = async () => {
  const isDelivery = localStorage.getItem('order') === 'delivery'
  const items = getPreparedCartToEstimate(store.cart)

  const pointIdKey = isDelivery ? 'address_id' : 'pickup_point_id'

  console.log('store.orderInfo', store.orderInfo)
  const payload: any = {
    ...store.orderInfo,
    items,
    [pointIdKey]: store.selectedLocation?.id || 0,
    promocode: store.promo?.code || null,
    with_stoplists: true,
  }

  if (store.desiredTime && !isDelivery) {
    payload['desired_time'] = store.desiredTime
  }

  if (store.cartComment) {
    payload['comment'] = store.cartComment
  }

  console.log('hewar!', items)
  const { data, status, code } = await orderEstimate(payload)

  if (status === 'ERR') {
    productError.value = true
    if (estimateErrors.hasOwnProperty(code)) {
      errorText.value = estimateErrors[code as keyof typeof estimateErrors] as string
    }

    if (code == 22) {
      data.items.forEach((item: string) => {
        errorText.value += ` (${item})`
      })
    }

    console.log('errorText.value', errorText.value)

    throw new Error(
      JSON.stringify({
        code: code.toString(),
        data,
      })
    )
  } else {
    productError.value = false
  }

  return data
}

watch(
  () => [store.selectedLocation, store.desiredTime, store.cartComment],
  () => {
    makeEstimate()
  }
)
</script>

<template>
  <div class="order">
    <div class="order__info info">
      <div class="info__address">
        <common-title :title="titles[orderType()]" />
        <order-address-picker
          :order-type="orderType()"
          @update-address-info="onUpdateAddressInfo"
        />
      </div>
      <!-- <div class="info__payment">
        <common-title title="Способ оплаты" />
        <order-payment-methods :order-type="orderType()" @update-method="onUpdatePayment" />
      </div> -->
      <div class="buttons">
        <button-main :button-state="5" @click="router.push('/?state=cart')">
          Назад в корзину
        </button-main>
        <button-main :disabled="isButtonDisabled && checkIsOpen()" @click="checkout">
          {{ buttonText }}
        </button-main>
      </div>
    </div>
    <order-list
      :cart-items="store.cart"
      :discount="discountValue"
      :isDeliveryAmount="isDeliveryAmount"
      :productError="productError"
      :errorText="errorText"
    />
    <auth-group v-if="isAuthVisible" @close="isAuthVisible = false" />
  </div>

  <teleport to="body">
    <div v-if="isPayment" class="order__payment">
      <div class="text">
        Ждём подтверждения оплаты
        <div class="loader"></div>
      </div>
    </div>
  </teleport>

  <teleport v-if="isMount" to=".sub-header__chips">
    <li
      v-if="store.categories.length"
      v-for="(food, index) of store.categories"
      :id="`chip-index-${index}`"
      ref="categoriesChipsElements"
      class="category-chip"
    >
      <chip-tag bigger @click="$router.push('/')">
        {{ food.name }}
      </chip-tag>
    </li>
    <div v-else class="chips-skeletons">
      <skeleton-chip v-for="_ in 6" />
    </div>
  </teleport>
</template>

<style scoped lang="scss">
.order {
  display: flex;
  gap: 16px;
  width: 100%;
  max-width: 1328px;
  margin: auto;
  padding: 64px 12px;
  height: calc(100vh - 118px);

  .info {
    display: flex;
    flex-direction: column;
    gap: 40px;
    width: 63.2%;

    &__address,
    &__payment {
      display: flex;
      flex-direction: column;
      gap: 32px;
    }
  }

  &__payment {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: rgba(0, 0, 0, 0.3);
    backdrop-filter: blur(3px);
    z-index: 10;

    display: flex;
    align-items: center;
    justify-content: center;

    color: black;
    @include h4-6;

    .text {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 32px;
      border-radius: 8px;
      padding: 48px 24px;
      background-color: $white;
    }
  }
}

.buttons {
  display: flex;
  gap: 32px;
  width: 100%;
  max-width: 640px;
  margin-top: auto;

  button {
    width: 100%;
  }
}

// todo вынести в компонент
.loader {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: transparent;
  border-radius: 50%;
}

.loader {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: transparent;
  width: 36px;
  height: 36px;
}

.loader {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: transparent;
  width: 36px;
  height: 36px;
  animation: spin89345 1s linear infinite;
}

@keyframes spin89345 {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}
</style>
